import React from 'react';
import ExcelJS from 'exceljs';
import axios from 'axios';
import config from '../config/config';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';

const ReportExportButton = () => {
  // Helper: convierte a Date o null
  const toDate = (v) => (v ? new Date(v) : null);

  // Helper: obtiene letra de columna tipo Excel (1 -> A, 27 -> AA)
  const getExcelColLetter = (colIndex) => {
    let dividend = colIndex;
    let columnName = '';
    let modulo;
    while (dividend > 0) {
      modulo = (dividend - 1) % 26;
      columnName = String.fromCharCode(65 + modulo) + columnName;
      dividend = Math.floor((dividend - modulo) / 26);
    }
    return columnName;
  };

  const handleExportButtonClick = async () => {
    try {
      const response = await axios.post(`${config.apiBaseUrl}/reporteTesorero`);
      const reportData = response.data || [];

      if (reportData.length === 0) {
        console.error('No hay datos para exportar');
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Reporte');

      // Encabezados (mantengo CONCEPTO_FLUJO por si luego lo devuelves del backend)
      const headers = [
        'ORDEN', 'FECHA_OC', 'TOTAL_ORDEN', 'TC', 'MONTO_PROGRAMAR', 'MONEDA', 'DESCRIPCION',
        'CENTRO_COSTOS', 'SOLICITANTE', 'RECEPCION', 'ESTATUS', 'EXHIBIT', 'FECHA_PAGO',
        'PROVEEDOR', 'RFC', 'BANCO', 'CUENTA', 'CLABE', 'SWIFT', 'ABA', 'COMENTARIOS',
        'CONCEPTO_FLUJO', 'FECHA_ENVIO_CARTA', 'FACTURA', 'POLIZA_ERP', 'COMPLEMENTO_PAGO'
      ];

      const headerRow = worksheet.addRow(headers);
      headerRow.eachCell((cell, colNumber) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: colNumber === 5 || colNumber === 12 ? '4682B4' : '003366' },
        };
        cell.font = { name: 'Calibri', size: 10, color: { argb: 'FFFFFF' }, bold: true };
        cell.border = {
          top: { style: 'thin' }, left: { style: 'thin' },
          bottom: { style: 'thin' }, right: { style: 'thin' },
        };
        cell.alignment = { vertical: 'middle' };
      });

      // Insertamos filas (usando Date reales en columnas de fecha)
      reportData.forEach((item) => {
        const row = [
          item.ORDEN,
          toDate(item.FECHA_OC),                 // FECHA_OC -> Date
          item.TOTAL_ORDEN,
          item.TC || '',
          item.MONTO_PROGRAMAR,
          item.MONEDA,
          item.DESCRIPCION,
          item.CENTRO_COSTOS,
          item.SOLICITANTE,
          toDate(item.RECEPCION),                // RECEPCION (p.FECHASOLICITUD) -> Date
          item.ESTATUS,
          item.EXHIBIT,
          toDate(item.FECHA_PAGO),               // FECHA_PAGO -> Date
          item.PROVEEDOR || '',
          item.RFC || '',
          item.BANCO || '',
          item.CUENTA || '',
          item.CLABE || '',
          item.SWIFT || '',
          item.ABA || '',
          item.COMENTARIOS || '',
          item.CONCEPTO_FLUJO || '',            // por si no viene del backend
          toDate(item.FECHA_ENVIO_CARTA),       // FECHA_ENVIO_CARTA -> Date
          item.FACTURA || '',
          item.POLIZA_ERP || '',
          item.COMPLEMENTO_PAGO || ''
        ];

        const dataRow = worksheet.addRow(row);
        dataRow.eachCell((cell) => {
          cell.font = { name: 'Calibri', size: 10 };
          cell.alignment = { vertical: 'middle' };
        });
        dataRow.height = 20;
      });

      // Formatos de fecha por nombre de columna (dinámico)
      const dateCols = ['FECHA_OC', 'RECEPCION', 'FECHA_PAGO', 'FECHA_ENVIO_CARTA'];
      dateCols.forEach((colName) => {
        const idx = headers.indexOf(colName);
        if (idx !== -1) {
          worksheet.getColumn(idx + 1).numFmt = 'dd/mm/yyyy';
        }
      });

      // Anchos de columna
      const widths = [
        8, 12, 16, 16, 16, 10, 61, 27, 16, 12, 11, 11, 12, 30, 18, 15, 16,
        25, 25, 25, 25, 25, 12, 17, 18, 15
      ];
      worksheet.columns.forEach((col, index) => {
        col.width = widths[index] || 15;
      });

      // Autofiltro hasta la última columna
      const lastColLetter = getExcelColLetter(headers.length);
      worksheet.autoFilter = `A1:${lastColLetter}1`;

      const buffer = await workbook.xlsx.writeBuffer();
      const dataBlob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Reporte.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error al obtener los datos del reporte:', error);
    }
  };

  return (
    <Tooltip title="Exportar Reporte" arrow>
      <span>
        <IconButton
          onClick={handleExportButtonClick}
        size="small"
        sx={{
          height: 28,
          border: '1px solid',
          borderRadius: '8px',
          backgroundColor: '#1D6F42',
          color: 'white',
          padding: '2px 8px',
          minWidth: 'unset',
          '&:hover': { backgroundColor: '#14532D' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <SimCardDownloadIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'white' }}>
            Excel
          </Typography>
        </Box>
      </IconButton>
      </span>
    </Tooltip>
  );
};

export default ReportExportButton;